import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPublicSubscriptionPackages } from '../../redux/subscriptionSlice';
import { BILLING_PERIODS, PACKAGE_ACCENTS } from '../../data/accessControls';
import PackageCardView from './PackageCardView';
import ContactModal from './ContactModal';
import { subscriptionPrice } from '../../utils/currency';

const priceLabel = (p) => {
  if (!p || p.price == null || String(p.price) === '') return 'On request';
  return String(p.price) === '0' ? 'Free' : subscriptionPrice(p.price, p);
};
const cardPrice = (p) => {
  if (p.purchasable === false) return 'Custom pricing';
  const bp = p.billingPeriods || {};
  const prices = BILLING_PERIODS.filter((x) => bp[x.id] && bp[x.id].enabled)
    .map((x) => Number(bp[x.id].price)).filter((n) => Number.isFinite(n));
  return prices.length ? `from ${subscriptionPrice(Math.min(...prices), p)}` : priceLabel(p);
};
const periodHint = (p) => {
  const bp = p.billingPeriods || {};
  const labels = BILLING_PERIODS.filter((x) => bp[x.id] && bp[x.id].enabled).map((x) => x.label);
  return labels.length ? labels.join(' · ') : `${p.durationDays || 30} days`;
};

// Public pricing section for the pre-login landing page. Visitors can't pay here —
// standard plans send them to log in, custom plans open the contact form.
const LandingPackages = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const publicPackages = useSelector((s) => s.subscription.publicPackages);
  const [contactPkg, setContactPkg] = useState(null);

  useEffect(() => {
    dispatch(getPublicSubscriptionPackages());
  }, [dispatch]);

  // Show paid + custom plans (skip the Free default trial).
  const plans = (publicPackages || []).filter((p) => p.isActive !== false && !p.isDefault);
  if (!plans.length) return null;

  return (
    <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8 bg-[#f0faf9]" data-testid="home-pricing-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="gradient-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="home-pricing-title">
            {t('home.pricing_title', 'Plans & pricing')}
          </h2>
          <p className="text-lg text-gray-600">
            {t('home.pricing_subtitle', 'Choose a plan that fits your practice — or talk to us about a custom one.')}
          </p>
          {/* Said on the landing page, not only after signing in: a visitor
              deciding on a plan needs to know there is no card step at all. */}
          <p className="text-sm text-gray-500 mt-2" data-testid="home-pricing-payment-note">
            {t('home.pricing_payment_note', 'No card needed — request a plan and we e-mail you an invoice. Pay by UPI or bank transfer and your plan is activated.')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 items-stretch">
          {plans.map((p) => {
            const isCustom = p.purchasable === false;
            const a = PACKAGE_ACCENTS[p.accent] || PACKAGE_ACCENTS.teal;
            const renderPrice = (onAccent) => (
              <div className="mt-1">
                <span className={`${isCustom ? 'text-xl' : 'text-2xl'} font-black ${onAccent ? 'text-white' : a.text}`}>{cardPrice(p)}</span>
                <p className={`text-xs mt-0.5 ${onAccent ? 'text-white/80' : 'text-slate-400'}`}>
                  {isCustom ? t('home.pricing_tailored', 'Tailored to your needs') : periodHint(p)}
                </p>
              </div>
            );
            return (
              <PackageCardView
                key={p.id}
                pkg={p}
                cardTestId={`home-plan-${p.name}`}
                renderPrice={renderPrice}
                footer={
                  isCustom ? (
                    <button
                      type="button"
                      onClick={() => setContactPkg(p)}
                      data-testid={`home-contact-${p.name}`}
                      className="w-full px-3 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:shadow-lg transition-all"
                    >
                      {t('home.pricing_contact', 'Contact us')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      // THE PLAN THEY CLICKED IS THE PLAN THEY LAND ON. The
                      // button used to drop the visitor on /login and forget
                      // why they came; Login honours state.next after a
                      // successful sign-in, so the plan page opens with the
                      // package already named.
                      onClick={() => navigate('/login', { state: { next: '/mySubscription', packageId: p.id } })}
                      data-testid={`home-subscribe-${p.name}`}
                      className="w-full px-3 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:shadow-lg transition-all"
                    >
                      {t('home.pricing_login_to_subscribe', 'Log in to request this plan')}
                    </button>
                  )
                }
              />
            );
          })}
        </div>
      </div>

      {contactPkg && (
        <ContactModal pkg={contactPkg} source="landing" onClose={() => setContactPkg(null)} />
      )}
    </section>
  );
};

export default LandingPackages;
